/**
 * Widget Configuration Service
 * Handles provider widget configuration CRUD operations
 */

import { apiClient, ApiClient } from './api';
import type {
  WidgetConfiguration,
  WidgetConfigResponse,
  UpdateBrandingDTO,
  UpdateServicesDTO,
  UpdatePricingDTO,
  UpdateBusinessInfoDTO,
  UpdateEmbedSettingsDTO,
  EmbedCodeResponse,
  LogoUploadResponse,
} from '../types/widget-config';

/**
 * WidgetConfigService class
 *
 * Provides methods to manage provider widget configuration.
 * Requires provider authentication.
 */
export class WidgetConfigService {
  private client: ApiClient;

  constructor(client: ApiClient = apiClient) {
    this.client = client;
  }

  /**
   * Get current provider's widget configuration
   *
   * @returns Promise<WidgetConfiguration> - Widget configuration
   */
  async getConfig(): Promise<WidgetConfiguration> {
    const response = await this.client.get<WidgetConfigResponse>('/provider/config');
    return response.config;
  }

  /**
   * Update branding settings
   *
   * @param branding - Branding settings to update
   * @returns Promise<WidgetConfiguration> - Updated configuration
   */
  async updateBranding(branding: UpdateBrandingDTO): Promise<WidgetConfiguration> {
    const response = await this.client.put<WidgetConfigResponse>(
      '/provider/config/branding',
      branding
    );
    return response.config;
  }

  /**
   * Update service offerings
   *
   * @param services - Service offerings to update
   * @returns Promise<WidgetConfiguration> - Updated configuration
   */
  async updateServices(services: UpdateServicesDTO): Promise<WidgetConfiguration> {
    const response = await this.client.put<WidgetConfigResponse>(
      '/provider/config/services',
      services
    );
    return response.config;
  }

  /**
   * Update pricing configuration
   *
   * @param pricing - Pricing configuration to update
   * @returns Promise<WidgetConfiguration> - Updated configuration
   */
  async updatePricing(pricing: UpdatePricingDTO): Promise<WidgetConfiguration> {
    const response = await this.client.put<WidgetConfigResponse>(
      '/provider/config/pricing',
      pricing
    );
    return response.config;
  }

  /**
   * Update business information
   *
   * @param businessInfo - Business information to update
   * @returns Promise<WidgetConfiguration> - Updated configuration
   */
  async updateBusinessInfo(businessInfo: UpdateBusinessInfoDTO): Promise<WidgetConfiguration> {
    const response = await this.client.put<WidgetConfigResponse>(
      '/provider/config/business-info',
      businessInfo
    );
    return response.config;
  }

  /**
   * Update embed settings
   *
   * @param embedSettings - Embed settings to update
   * @returns Promise<WidgetConfiguration> - Updated configuration
   */
  async updateEmbedSettings(embedSettings: UpdateEmbedSettingsDTO): Promise<WidgetConfiguration> {
    const response = await this.client.put<WidgetConfigResponse>(
      '/provider/config/embed',
      embedSettings
    );
    return response.config;
  }

  /**
   * Get embed code for widget
   *
   * @returns Promise<EmbedCodeResponse> - Embed code and URLs
   */
  async getEmbedCode(): Promise<EmbedCodeResponse> {
    return this.client.get<EmbedCodeResponse>('/provider/config/embed-code');
  }

  /**
   * Upload logo image
   *
   * @param file - Logo image file
   * @returns Promise<LogoUploadResponse> - Upload result with logo URL
   */
  async uploadLogo(file: File): Promise<LogoUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    // Override Content-Type to let browser set multipart boundary
    const response = await fetch(
      `${this.client['baseUrl']}/provider/config/logo-upload`,
      {
        method: 'POST',
        body: formData,
        // Don't set Content-Type - browser will set it with boundary
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: 'Upload failed',
      }));
      throw new Error(error.message || 'Failed to upload logo');
    }

    return response.json();
  }

  /**
   * Upload hero image
   *
   * @param file - Hero image file
   * @returns Promise<{success: boolean; url: string}> - Upload result
   */
  async uploadHeroImage(file: File): Promise<{ success: boolean; heroImageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${this.client['baseUrl']}/provider/config/hero-image-upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: 'Upload failed',
      }));
      throw new Error(error.message || 'Failed to upload hero image');
    }

    return response.json();
  }
}

// Export singleton instance
export const widgetConfigService = new WidgetConfigService();
